import type React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface CustomAvatarProps {
  name: string;
  imageUrl?: string;
  size?: number;
  className?: string;
  roundedFull?: true;
}

/**
 * カスタムアバターコンポーネント
 * Shadcn UIのAvatarコンポーネントをラップして使いやすくしたもの
 */
export const CustomAvatar: React.FC<CustomAvatarProps> = ({
  name,
  imageUrl,
  size = 32,
  className = "",
  roundedFull,
}) => {
  // 名前から初期文字を抽出
  const getInitials = (name: string) => {
    const nameParts = name.split(/\s+/);
    if (nameParts.length === 1) {
      return name.charAt(0).toUpperCase();
    }
    return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
  };

  // 名前からランダムな背景色を生成
  const getBackgroundColor = (name: string) => {
    const colors = [
      "var(--rw-avatar-red-400)",
      "var(--rw-avatar-orange-400)",
      "var(--rw-avatar-amber-400)",
      "var(--rw-avatar-emerald-400)",
      "var(--rw-avatar-blue-400)",
      "var(--rw-avatar-violet-400)",
      "var(--rw-avatar-pink-400)",
    ];

    // 名前の文字コードを合計して色を選択
    const sum = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[sum % colors.length];
  };

  const bgColor = getBackgroundColor(name);
  const initials = getInitials(name);

  const sizeStyle = {
    width: `${size}px`,
    height: `${size}px`,
    fontSize: `${Math.floor(size / 2.5)}px`,
  };

  return (
    <Avatar
      className={cn(className, roundedFull && "rounded-full")}
      style={sizeStyle}
    >
      {imageUrl && (
        <AvatarImage
          src={imageUrl}
          alt={name}
          onError={(e) => {
            // 画像読み込みエラー時にはフォールバックを表示する
            e.currentTarget.style.display = "none";
          }}
        />
      )}
      <AvatarFallback
        style={{
          backgroundColor: bgColor,
          color: "white",
        }}
        className={cn(roundedFull && "rounded-full")}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};
